import { useQuery } from "@tanstack/react-query";
import {
  listAgentActiveMissions,
  listAgentMissionHistory,
} from "../services/agentMissionService";
import { listMyAgentTrips } from "@/features/trips";
import { splitAgentTrips } from "@/features/trips/lib/offerPolicy";
import { AGENT_TRIPS_QUERY_KEY } from "./useAgentOffers";
import { getAgent } from "../services/agentService";
import { useSessionStore } from "../../../shared/stores/sessionStore";

export const AGENT_QUERY_KEY = ["agent"] as const;

// Missions actives assignées à l'agent (toutes étapes confondues).
//
// ⚠️ SONDAGE ASSUMÉ, malgré R11 du contrat du 2026-08-26. La clause interdit de
// remplacer l'ÉCOUTE par du sondage ; elle n'interdit pas de sonder ce qui
// n'est pas écoutable. Or c'est le cas ici : le backend AUTO-ASSIGNE les
// courses, `trip.agent.assigned` est porté par `trip:{id}`, et l'agent ne peut
// pas rejoindre la salle d'une course dont il ignore encore l'identifiant.
// Aucun événement documenté ne couvre la DÉCOUVERTE d'une mission.
//
// La cadence n'est donc pas relâchée : une mission qui apparaît une minute plus
// tard, c'est une course perdue pour l'agent. Une fois la mission ouverte,
// `useTrip` prend le relais en temps réel et ce sondage n'intervient plus.
//
// À revoir dès qu'un événement d'affectation atteindra `user:{sub}`.
export function useAgentMissions(enabled = true) {
  const agentId = useSessionStore((s) => s.agentId);
  return useQuery({
    queryKey:  [...AGENT_QUERY_KEY, "missions", agentId],
    queryFn:   () => listAgentActiveMissions(agentId!),
    enabled:   enabled && Boolean(agentId),
    staleTime: 15_000,
    refetchInterval: enabled ? 20_000 : false,
  });
}

// Courses multi-arrêts ACTIVES de la Main. Volontairement NON gardée par le
// toggle online : une course en cours est un engagement (fonds client bloqués)
// que l'agent doit pouvoir terminer même s'il s'est mis hors ligne.
//
// ⚠️ PLUS DE FILTRE `agent_id`. Le contrat du 2026-08-28 est formel : ce
// paramètre est SILENCIEUSEMENT IGNORÉ pour une Main — l'identité vient du
// jeton. On croyait filtrer, on ne filtrait pas ; et le `enabled` qui attendait
// la résolution du profil retardait la liste pour rien.
//
// La requête rejoint le flux unique (`useAgentTripsFeed`) : offres et courses
// attribuées viennent de la MÊME réponse — `GET /trips` rend leur union — et
// partagent donc son cache. Deux écrans montés ensemble ne déclenchent qu'un
// appel, à la cadence de 20 à 30 s que le contrat impose comme filet quand le
// socket est tombé sans le dire.
export function useAgentActiveTrips() {
  return useQuery({
    queryKey: AGENT_TRIPS_QUERY_KEY,
    queryFn:  () => listMyAgentTrips(),
    staleTime: 5_000,
    refetchInterval: 25_000,
    refetchOnWindowFocus: true,
    // Seules les courses réellement attribuées : une offre en attente n'est pas
    // encore une course, et l'afficher comme telle ferait croire à un engagement
    // que la Main n'a pas pris.
    select: (trips) => splitAgentTrips(trips, Date.now()).assigned,
  });
}

// Historique (status DELIVERED uniquement — l'écran "Done" côté agent)
export function useAgentMissionHistory() {
  const agentId = useSessionStore((s) => s.agentId);
  return useQuery({
    queryKey:  [...AGENT_QUERY_KEY, "missions", "history", agentId],
    queryFn:   () => listAgentMissionHistory(agentId!),
    enabled:   Boolean(agentId),
    staleTime: 30_000,
  });
}

// Profil agent (KYC, score, charge, floating_cash_balance)
export function useAgentProfile() {
  const agentId = useSessionStore((s) => s.agentId);
  return useQuery({
    queryKey:  [...AGENT_QUERY_KEY, "profile", agentId],
    queryFn:   () => getAgent(agentId!),
    enabled:   Boolean(agentId),
    staleTime: 60_000,
  });
}
